import {
  Box,
  Button,
  Card,
  CircularProgress,
  Grid,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { Check, Receipt, ReceiptOutlined } from "@material-ui/icons";
import { Autocomplete } from "@material-ui/lab";
import { useHeader } from "components/Layout/HeaderContext";
import { RootState } from "config/store";
import { round } from "lodash";
import { useSnackbar } from "notistack";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { listCurrencyByTenant } from "shared/network/currency.api";
import { invoiceGenerateFromOrder } from "shared/network/invoice.api";
import { createSelling, modifySelling } from "shared/network/order.api";
import { listWarehousePageable } from "shared/network/warehouse.api";
import {
  BaseCurrency,
  ItemCategory,
  ItemPackaging,
  ItemPriceHistory,
  ItemTaxHistory,
  Order,
  Packaging,
  Warehouse,
} from "shared/types";
import { removeEmptyStrings } from "shared/util/removeEmptyStrings";
import { useDebouncedCallback } from "use-debounce";
import SellingFormCategories from "./SellingFormCategoryList/SellingFormCategoryList";
import SellingFormItemList from "./SellingFormItemList";
import SellingFormItems from "./SellingFormItems";
import SellingPaymentType from "./SellingPaymentType";
import { CashRegister, getPageCashRegister } from "shared/network/cash-register.api";
import queryClient from "config/query";
import ConfirmationButton from "components/ConfirmationButton";

export type SellingFormValues = {
  order: {
    isPaid: boolean;
    currency: BaseCurrency;
    orderName: string;
    isOneItem: boolean;
  };
  relOrderItem: any[]; //OrderItemEntry[];
  fullNetPrice: string;
  fullGrossPrice: string;
  paymentType: string;
  showComment: boolean;
  showPackageSize: boolean;
  warehouse: Warehouse | null;
  cashRegister: CashRegister | null;
};

type Props = {
  paymentType: any;
  setPaymentType: any;
  totalPrice: number;
  order: Order | null;
  saveOrder: any;
  setGroupedItems: Dispatch<SetStateAction<ReceiptItem[]>>;
  setIsBlockDialogOpen: Dispatch<SetStateAction<boolean>>;
};

export type ReceiptItem = {
  tax: any;
  items: any;
  total: any;
};

const SellingForm = ({
  paymentType,
  setPaymentType,
  totalPrice,
  order,
  setGroupedItems,
  setIsBlockDialogOpen,
}: Props) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const tenant = useSelector((state: RootState) => state.authentication?.selectedRelTenant?.tenant);

  const form = useFormContext<SellingFormValues>();
  const { control, watch, trigger, getValues } = form;

  const relOrderItemFieldArray = useFieldArray({
    control,
    name: "relOrderItem",
  });
  const { fields, append, remove } = relOrderItemFieldArray;
  const currency = watch("order.currency");

  const [selectedCategory, setSelectedCategory] = useState<ItemCategory | null>(null);

  const currencyListQuery = useQuery(["currencyListForOffer", tenant.id], async () => {
    const { data } = await listCurrencyByTenant(0, 1000000, tenant.id, "");
    return data.page.content;
  });

  const newWindowRef = useRef<Window | null>(null);

  const openOrUpdateWindow = (orderId: number | undefined) => {
    if (!newWindowRef.current || newWindowRef.current.closed) {
      newWindowRef.current = window.open(
        `/selling-price?id=${orderId}`,
        "_blank",
        "width=500,height=400",
      );
    }

    if (newWindowRef.current) {
      newWindowRef.current.document.close();
    }
  };

  async function saveOrder(values: SellingFormValues, openWindow: boolean) {
    try {
      if (!order) {
        const { data } = await createSelling(
          {
            ...removeEmptyStrings(values),
            order: { ...values.order, paymentType },
            relOrderItem: values.relOrderItem.map(item => {
              return {
                ...item,
                item: item.item,
                quantity: Number(item.quantity || 1),
              };
            }),
          },
          tenant.id,
        );
        if (data?.item?.id) {
          openOrUpdateWindow(data?.item?.id);
          history.replace(`/selling?id=${data?.item?.id}`);
        }
      } else {
        const { data } = await modifySelling(
          {
            ...removeEmptyStrings(values),
            order: { ...order, ...values.order, paymentType },
            relOrderItem: values.relOrderItem.map(item => {
              return {
                ...item,
                item: item.item,
                quantity: Number(item.quantity || 1),
              };
            }),
          },
          tenant.id,
        );
        if (data?.item?.id && openWindow) {
          openOrUpdateWindow(data?.item?.id);
        }
      }
    } catch (e: any) {}
  }

  const [loading, setLoading] = useState(false);
  async function invoiceGenerate(isRequester: boolean) {
    const result = await trigger();
    if (result) {
      await saveOrder(getValues(), false);
      setLoading(true);
      try {
        if (order) {
          const { data } = await invoiceGenerateFromOrder({
            orderId: order.id,
            tenantId: tenant.id,
          });

          if (data?.invoice) {
            history.push(
              `/invoices/invoices-list/invoice-create?isLight=true&isRequester=${isRequester}`,
              data,
            );
          } else {
            enqueueSnackbar(t("contract.noInvoiceData"), { variant: "info" });
          }
        }
      } catch {
        enqueueSnackbar(t("contract.invoiceError"), {
          variant: "error",
        });
      }
      setLoading(false);
    }
  }

  const { setHeaderButtons } = useHeader();
  useEffect(() => {
    setHeaderButtons(
      <Box display="flex" gridGap={12}>
        <Button
          variant="outlined"
          color="primary"
          //@ts-ignore
          onClick={() => {
            if (order?.id) {
              openOrUpdateWindow(order?.id);
            } else {
              //@ts-ignore
              form.handleSubmit(saveOrder(form.watch(), true))();
            }
          }}
        >
          Összeg nézet
        </Button>
      </Box>,
    );
    return () => {
      setHeaderButtons(null);
    };
  }, [order?.id]);

  const relOrderItems = watch("relOrderItem");

  const handleBlock = () => {
    // Termékek csoportosítása adókulcs alapján
    const grouped = relOrderItems.reduce((acc: any, item: any) => {
      const tax =
        item.item?.itemTaxHistories?.find((tax: ItemTaxHistory) => tax.isActive)?.tax?.percent || 0;
      if (!acc[tax]) {
        acc[tax] = [];
      }
      acc[tax].push(item);
      return acc;
    }, {});

    const groupedArray = Object.entries(grouped).map(([tax, items]: any) => ({
      tax,
      items,
      total: items.reduce((sum: number, item: any) => {
        const priceHistory = item.item.itemPriceHistories.find(
          (price: ItemPriceHistory) => price.isActive && currency.id === price?.currency?.id,
        );
        const price = priceHistory?.price ? Number(priceHistory?.price) : 0;
        const tax =
          (item.item?.itemTaxHistories?.find((tax: ItemTaxHistory) => tax.isActive)?.tax?.percent ||
            0) / 100 || 0;
        const grossPrice = round(price * (1 + tax), 2);

        return sum + item.quantity * (grossPrice || 0);
      }, 0),
    }));

    setGroupedItems(groupedArray);
    setIsBlockDialogOpen(true);
  };
  const [warehouseSearch, setWarehouseSearch] = useState("");
  const [warehouseInitialized, setWarehouseInitialized] = useState(false);

  const warehouseListQuery = useQuery(
    ["warehouseListForCargo", tenant.id, warehouseSearch],
    async () => {
      const { data } = await listWarehousePageable(
        0,
        20,
        tenant.id,
        warehouseSearch ? `name:${warehouseSearch}` : ``,
      );
      return data.page.content;
    },
    {
      onSuccess: data => {
        if (!warehouseInitialized && !getValues("warehouse")) {
          form.setValue("warehouse", data?.[0]);
          setWarehouseInitialized(true);
          form.clearErrors();
        }
      },
    },
  );

  const handleWarehouseSearchChange = useDebouncedCallback((value: string) => {
    if (value.length >= 2 || value.length === 0) {
      setWarehouseSearch(value);
    }
  }, 500);

  const [cashRegisterSearch, setCashRegisterSearch] = useState("");
  const [cashRegisterInitialized, setCashRegisterInitialized] = useState(false);

  const cashRegisterListQuery = useQuery(
    ["cashRegisterList", tenant?.id, cashRegisterSearch],
    async () => {
      const { data } = await getPageCashRegister({
        page: 0,
        size: 2000,
        tenantId: tenant?.id,
        search: cashRegisterSearch ? `name:$${cashRegisterSearch}` : "",
      });
      return data?.page?.content?.sort((a, b) => a?.name?.localeCompare(b?.name));
    },
    {
      onSuccess: data => {
        if (!cashRegisterInitialized && !getValues("cashRegister")) {
          const defaultCashRegister = data?.find(value => value?.isDefault);
          form.setValue("cashRegister", defaultCashRegister || data?.[0]);
          setCashRegisterInitialized(true);
          form.clearErrors();
        }
      },
    },
  );

  const handleCashRegisterSearchChange = useDebouncedCallback((value: string) => {
    if (value.length >= 2 || value.length === 0) {
      setCashRegisterSearch(value);
    }
  }, 500);

  return (
    <>
      <Box display="flex" justifyContent="center" flexWrap="wrap" gridGap={8} mb={2}>
        <Controller
          name="cashRegister"
          defaultValue={null}
          rules={{ required: t("validation.required").toString() }}
          render={({ field, fieldState }) => (
            <Autocomplete
              {...field}
              onChange={(_, value) => field.onChange(value)}
              onInputChange={(_, newInputValue) => handleCashRegisterSearchChange(newInputValue)}
              options={cashRegisterListQuery.data || []}
              getOptionLabel={option => option?.name || ""}
              getOptionSelected={option => option.id === field.value?.id}
              renderInput={params => (
                <TextField
                  {...params}
                  style={{ width: 300 }}
                  InputLabelProps={{ shrink: true, required: true }}
                  label="Pénztár"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          )}
        />
        <Controller
          name="warehouse"
          defaultValue={null}
          rules={{ required: t("validation.required").toString() }}
          render={({ field, fieldState }) => (
            <Autocomplete
              {...field}
              onChange={(_, value) => {
                field.onChange(value);
                handleWarehouseSearchChange("");
                remove();
              }}
              onInputChange={(event, newInputValue) => {
                handleWarehouseSearchChange(newInputValue);
              }}
              options={warehouseListQuery.data || []}
              getOptionLabel={(option: Warehouse) => option.name}
              getOptionSelected={option => option.id === field.value?.id}
              renderInput={params => (
                <TextField
                  {...params}
                  style={{ width: 300 }}
                  InputLabelProps={{ shrink: true, required: true }}
                  label={t("cargo.formValues.warehouse")}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          )}
        />
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={8}>
          <Card style={{ marginBottom: 16 }}>
            <SellingFormCategories
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
            />
          </Card>
          <Card>
            <SellingFormItemList
              selectedCategory={selectedCategory}
              selectedCurrency={currency}
              append={append}
            />
          </Card>
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <Card style={{ padding: 16, height: "100%" }}>
            <Box display="flex" alignItems="center" width="100%">
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12}>
                  <Controller
                    control={control}
                    name="order.currency"
                    defaultValue={tenant.baseCurrency}
                    rules={{ required: t("validation.required").toString() }}
                    render={({ field, fieldState }) => (
                      <Autocomplete
                        {...field}
                        disableClearable
                        onChange={(_, value) => field.onChange(value)}
                        options={currencyListQuery.data || []}
                        getOptionLabel={(option: BaseCurrency) => option.name}
                        getOptionSelected={option => option.id === field.value?.id}
                        renderInput={params => (
                          <TextField
                            {...params}
                            label={t("offer.formValues.currency")}
                            InputLabelProps={{ shrink: true, required: true }}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                          />
                        )}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Box>
            <SellingFormItems fields={fields} remove={remove} selectedCurrency={currency} />
            <Box display="flex" justifyContent="space-between" alignItems="center" pb={2} pt={1}>
              <Typography style={{ fontWeight: "bold", fontSize: 16 }}>
                {t("order.formValues.fullGrossPrice")}
                {": "}
              </Typography>
              <Typography style={{ fontWeight: "bold", fontSize: 18 }}>
                {(currency?.prefix ? currency.prefix + " " : "") +
                  t("common:number", { num: totalPrice }) +
                  (currency?.postfix ? " " + currency.postfix : "")}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" paddingBottom={2}>
              <SellingPaymentType paymentType={paymentType} setPaymentType={setPaymentType} />
            </Box>
            <Box
              display="flex"
              justifyContent="center"
              flexDirection="column"
              width="100%"
              gridGap={8}
            >
              <Button fullWidth size="small" type="submit" color="primary" startIcon={<Check />}>
                {t("common:button.save")}
              </Button>
              <Button
                fullWidth
                size="small"
                disabled={!order || loading || fields.length === 0}
                variant="outlined"
                color="primary"
                onClick={() => invoiceGenerate(false)}
                startIcon={loading ? <CircularProgress size={16} /> : <Receipt />}
              >
                {t("invoiceCreate")}
              </Button>
              <Button
                fullWidth
                size="small"
                variant="outlined"
                color="primary"
                disabled={!order || loading || fields.length === 0}
                startIcon={<ReceiptOutlined />}
                onClick={handleBlock}
              >
                {t("order.buy.purchaseReceipt")}
              </Button>

              <ConfirmationButton
                color="primary"
                size="small"
                variant="outlined"
                onSubmit={() => {
                  form.reset({
                    relOrderItem: [],
                    warehouse: null,
                    cashRegister: null,
                    order: {
                      isPaid: false,
                      currency: tenant.baseCurrency,
                      orderName: "",
                      isOneItem: false,
                    },
                    fullNetPrice: "",
                    fullGrossPrice: "",
                    paymentType: "",
                    showComment: false,
                    showPackageSize: false,
                  });
                  setSelectedCategory(null);
                  setGroupedItems([]);
                  setIsBlockDialogOpen(false);
                  setWarehouseSearch("");
                  setWarehouseInitialized(false);
                  setCashRegisterSearch("");
                  setCashRegisterInitialized(false);
                  history.push("/selling");
                }}
                title="Új értékesítés"
                body="Biztosan új értékesítést kezd?"
              >
                Új értékesítés
              </ConfirmationButton>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </>
  );
};

export default SellingForm;
